<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useModals } from "@/utils/useModals";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { policyUrl, rulesUrl, agreementUrl } from "@/utils/provider";
import { useRoute, RouterLink, useRouter } from "vue-router";
import BaseInput from "@/components/ui/BaseInput.vue";
import BasePhoneInput from "@/components/ui/BasePhoneInput.vue";
import BaseCheckbox from "@/components/ui/BaseCheckbox.vue";
import BaseSelect from "@/components/ui/BaseSelect.vue";
import BaseTextarea from "@/components/ui/BaseTextarea.vue";
import BaseFile from "@/components/ui/BaseFile.vue";
import BasePassword from "@/components/ui/BasePassword.vue";
import BasePasswordRepeat from "@/components/ui/BasePasswordRepeat.vue";
import QuizCloseWebAppBtn from "@/components/quiz/QuizCloseWebAppBtn.vue";

const storeField = useFieldsStore();
const storeSettings = useSettingsStore();
const storeRequests = useRequestsStore();
const route = useRoute();

const selectOptions = ref([]);

const password = defineModel();

const { activeModal, modalId, modalObj, closeModal, openModal } = useModals();

const userReceiptsTitle = {
	upload_available: "Загрузка чека доступна",
	upload_unavailable: "Загрузка чека недоступна, акция неактивна",
	maximum_receipt: "Ты уже загрузили максимальное количество чеков за период акции — 20 штук.",
	maximum_receipt_in_current_period: "Ты достиг максимального количества загрузок чеков за один день — 3 штуки. Новые загрузки будут доступны завтра.",
	maximum_upload_receipt: "У тебя есть чеки, ожидающие проверки. Чтобы не превысить лимит, новые чеки можно будет загрузить после завершения модерации.",
	maximum_upload_receipt_in_current_period: "У тебя есть чеки, ожидающие проверки. Чтобы не превысить лимит, новые чеки можно будет загрузить после завершения модерации.",
};

watch(
	() => storeRequests.isAuthenticated,
	(newVal) => {
		if (newVal) {
			storeField.updateFields("firstName", "disabledBtn", true);
			storeField.updateFields("email", "disabledBtn", true);
			storeField.updateFields("lastName", "disabledBtn", true);
			storeField.updateFields("phone", "disabledBtn", true);
		}
	}
);

onMounted(async () => {});
</script>
<template>
	<div :class="['modal', { 'is-open': activeModal, '--white-modal': modalId === 'feedback' || modalId === 'foundNumberPhone' }]" @click.self="closeModal">
		<Transition>
			<div v-if="activeModal" :class="['modal__container', { '--w600': modalId === 'foundNumberPhone' }]">
				<button class="modal-close" @click="closeModal">
					<svg width="40" height="40"><use href="#x"></use></svg>
				</button>
				<!-- Обратная связь -->
				<div class="modal-content" v-if="modalId === 'feedback'">
					<h5 class="modal-title title">Связаться с техподдержкой</h5>
					<form class="modal-form form">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName" > Имя </BaseInput>
							</div>
							<div class="form__wrap">
								<BasePhoneInput :required="true" placeholder="+7 (900) 000-00-00" state-key="phone" >Номер телефона</BasePhoneInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" placeholder="example@mail.ru" :required="true" state-key="email" >Email</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseTextarea :max-length="0" state-key="message" placeholder="Комментарий">Текст сообщения</BaseTextarea>
							</div>
							<div class="form__wrap">
								<BaseFile :oneLine="true">Прикрепить файл</BaseFile>
							</div>
						</div>
						<div class="form__footer">
							<BaseCheckbox :required="true" state-key="agreement">Даю согласие на <a href="./policy.pdf" target="_blank" rel="noopener noreferrer">обработку персональных данных</a></BaseCheckbox>

							<button @click="storeRequests.feedback()"  :disabled="storeField.isBtnDisabled(['email', 'firstName', 'phone', 'message', 'agreement'])" :class="['send-button form__btn btn', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Отправить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
						</div>
					</form>
				</div>

				<!-- Авторизация -->
				<div class="modal-content" v-if="modalId === 'authorization'">
					<h5 class="modal-title">Авторизация</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.authorization()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="email" placeholder="Email" :required="true" state-key="email"> Логин/Email </BaseInput>
							</div>
							<div class="form__wrap">
								<BasePassword placeholder="Пароль (не менее 6 символов)" :required="true" state-key="password"> Пароль </BasePassword>
								<div class="form__text-btn-inner">
									<svg width="20" height="20"><use href="#icons_forgot_pass"></use></svg>
									<button @click="openModal('recoveryPassword')" class="form__text-btn" type="button">Забыли пароль?</button>
								</div>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['email', 'password'])" :class="['send-button form__btn btn --red', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Войти</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<div class="form__btn-wrapper" v-if="!storeSettings.statusStockPast">
								<span>
									У меня нет аккаунта
									<button @click="openModal('registration')" class="">Регистрация</button>
								</span>
							</div>
						</div>
					</form>
				</div>

				<!-- Требуется авторизация -->
				<div class="modal-content" v-if="modalId === 'authorizationRequired'">
					<h5 class="modal-title">Требуется авторизация</h5>
					<form class="form form-grid">
						<div class="form__footer">
							<div class="form__btn-wrapper">
								<span>У меня уже есть аккаунт</span>
								<button @click="openModal('authorization')" class="send-button btn form__btn --red" type="button">Войти</button>
							</div>
							<div class="form__btn-wrapper" v-if="!storeSettings.statusStockPast">
								<span>У меня нет аккаунта</span>
								<button @click="openModal('registration')" class="send-button btn form__btn --red" type="button">Регистрация</button>
							</div>
						</div>
					</form>
				</div>

				<!-- Регистрация -->
				<div class="modal-content" v-if="modalId === 'registration'">
					<h5 class="modal-title">Регистрация</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.registration()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName"> Имя</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Фамилия" :required="true" state-key="lastName">Фамилия</BaseInput>
							</div>
							<div class="form__wrap">
								<BasePhoneInput :required="true" placeholder="Номер телефона" state-key="phone">Номер телефона </BasePhoneInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="email" :required="true" placeholder="Email" state-key="email">Email </BaseInput>
							</div>
							<div class="form__wrap">
								<BasePassword placeholder="Пароль (не менее 6 символов)" :required="true" state-key="password" v-model="password"> Пароль </BasePassword>
							</div>
							<div class="form__wrap">
								<BasePasswordRepeat placeholder="Повторите пароль" :required="true" state-key="passwordRepeat" :original-password="computed(() => password)"> Повторите пароль </BasePasswordRepeat>
							</div>
						</div>
						<div class="form__checkboxes">
							<BaseCheckbox :required="true" state-key="agreement"> Я согласен(на) на <a :href="agreementUrl" target="_blank" rel="noopener noreferrer">обработку персональных данных</a> </BaseCheckbox>
							<BaseCheckbox :required="true" state-key="rules">Я согласен(на) с <a :href="rulesUrl" target="_blank" rel="noopener noreferrer">правилами акции</a> </BaseCheckbox>
							<BaseCheckbox :required="true" state-key="agreeToAdvertising">Я согласен(на) на маркетинговую рассылку</BaseCheckbox>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['firstName', 'lastName', 'phone', 'email', 'rules', 'agreement', 'passwordRepeat', 'agreeToAdvertising'])" :class="['send-button form__btn btn --red', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Регистрация</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<div class="form__btn-wrapper">
								<span>
									У меня уже есть аккаунт.
									<button @click="openModal('authorization')" class="">Войти</button>
								</span>
							</div>
						</div>
					</form>
				</div>

				<!-- Восстановление пароля -->
				<div class="modal-content" v-if="modalId === 'recoveryPassword'">
					<h5 class="modal-title">Восстановление пароля</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.recoveryPassword()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="email" :required="true" placeholder="Email" state-key="email">Email </BaseInput>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['email'])" :class="['send-button form__btn btn --red', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Отправить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
							<p class="form__text-error" v-if="storeRequests.responseError">{{ storeRequests.responseError }}</p>
							<button type="button" @click="openModal('authorization')" class="send-button form__btn btn --blue-border">Назад</button>
						</div>
					</form>
				</div>

				<!-- Изменение пароля -->
				<div class="modal-content" v-if="modalId === 'changeUserPassword'">
					<h5 class="modal-title">Изменение пароля</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.changeUserPassword()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BasePassword placeholder="Пароль (не менее 6 символов)" :required="true" state-key="password" v-model="password"> Новый пароль </BasePassword>
							</div>
							<div class="form__wrap">
								<BasePasswordRepeat placeholder="Повторите пароль" :required="true" state-key="passwordRepeat" :original-password="computed(() => password)"> Повторите новый пароль </BasePasswordRepeat>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['password', 'passwordRepeat'])" :class="['send-button form__btn btn --red', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Изменить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
						</div>
					</form>
				</div>

				<!-- Изменение данных -->
				<div class="modal-content" v-if="modalId === 'changeUserProfile'">
					<h5 class="modal-title">Изменение данных</h5>
					<form class="form form-grid" @submit.prevent="storeRequests.changeUserProfile()">
						<div class="form__block-inputns">
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Имя" :required="true" state-key="firstName" v-model="storeRequests.userData.first_name"> Имя</BaseInput>
							</div>
							<div class="form__wrap">
								<BaseInput type="text" placeholder="Фамилия" :required="true" state-key="lastName" v-model="storeRequests.userData.last_name">Фамилия</BaseInput>
							</div>
							<div class="form__wrap">
								<BasePhoneInput :required="true" placeholder="Номер телефона" state-key="phone">Номер телефона </BasePhoneInput>
							</div>
						</div>
						<div class="form__footer">
							<button :disabled="storeField.isBtnDisabled(['firstName', 'lastName', 'phone'])" :class="['send-button form__btn btn --red', { 'not-hover': storeRequests.loaderBtn }]">
								<span v-if="!storeRequests.loaderBtn">Изменить</span>
								<img v-else class="form__loader" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
							</button>
						</div>
					</form>
				</div>

				<!-- Регистрация завершена -->
				<div class="modal-content" v-if="modalId === 'registrationEnd'">
					<h5 class="modal-title">Регистрация завершена</h5>
					<p class="modal-subtitle">На указанный при регистрации e-mail отправлено письмо с данными для входа.</p>
					<p class="modal-subtitle">Пожалуйста, перейдите по ссылке в письме, чтобы активировать ваш аккаунт и ознакомиться с паролем.</p>
					<p class="modal-subtitle">Не нашли письмо? Проверьте папку «Спам» или запросите повторное письмо</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --red" @click="openModal('authorization')">Войти</button>
					</div>
				</div>

				<!-- Пройти верефикацию -->
				<div class="modal-content" v-if="modalId === 'notVerified'">
					<h5 class="modal-title">Ваш аккаунт ещё не активирован</h5>
					<p class="modal-subtitle">
						Пожалуйста, перейдите по ссылке из письма, отправленного на ваш e-mail, чтобы завершить регистрацию. <br />
						Не нашли письмо? Запросите повторную отправку.
					</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --red" type="button" @click="storeRequests.repeatVerification()">Запросить повторно</button>
					</div>
				</div>

				<!-- Восстановление пароля send Email -->
				<div class="modal-content" v-if="modalId === 'sentNewPassword'">
					<h5 class="modal-title">Запрос на восстановление пароля принят</h5>
					<p class="modal-subtitle">На ваш e-mail отправлено письмо с паролем для входа в личный кабинет.</p>
					<p class="modal-subtitle">Если письмо не пришло в течение нескольких минут, проверьте папку «Спам» или запросите повторное.</p>
					<div class="form form-grid">
						<button class="send-button btn form__btn --red" @click="openModal('authorization')">Войти</button>
					</div>
				</div>

				<!-- Изменения пароля send Email -->
				<div class="modal-content" v-if="modalId === 'sentChangeNewPassword'">
					<h5 class="modal-title">Изменение пароля</h5>
					<p class="modal-subtitle">Твой пароль изменен!</p>
				</div>

				<!-- Ответ на feedback -->
				<div class="modal-content" v-if="modalId === 'statusOk'">
					<h5 class="modal-title title">Обращение отправлено!</h5>
					<p class="modal-subtitle --center">Ваше обращение успешно отправлено.</p>
					<button @click="closeModal" class="btn form__btn --red">Хорошо</button>
				</div>

				<!-- Акция еще не началась-->
				<div class="modal-content" v-if="modalId === 'promoNotStart'">
					<h5 class="modal-title --center" style="text-align: center">Акция скоро начнётся!</h5>
					<p class="modal-subtitle --center">Уже совсем скоро сможешь загружать чеки и выигрывать призы. Следи за обновлениями!</p>
				</div>

				<!-- Avito как найти телефон -->
				<div class="modal-content" v-if="modalId === 'foundNumberPhone'">
					<h5 class="modal-title title" style="text-align: start">Как узнать номер телефона?</h5>
					<div class="modal-avito">
						<div class="modal-avito__block">
							<div class="modal-avito__header">
								<h6 class="modal-avito__title">В версии для компьютера</h6>
								<ul class="modal-avito__list">
									<li class="modal-avito__item">Перейдите по ссылке <a href="https://www.avito.ru/profile/settings" target="_blank" rel="noopener noreferrer">https://www.avito.ru/profile/settings</a></li>
									<li class="modal-avito__item">Номер будет указан в разделе «Телефоны»</li>
								</ul>
							</div>
							<div class="modal-avito__image">
								<img class="full-img" width="524" height="305" src="@/assets/img/avito/avito_desc.webp" srcset="@/assets/img/avito/avito_desc@2x.webp 2x" alt="изображение" />
							</div>
						</div>
						<div class="modal-avito__block --mobile">
							<div class="modal-avito__header">
								<h6 class="modal-avito__title">В мобильном приложении</h6>
								<ul class="modal-avito__list">
									<li class="modal-avito__item">Зайдите в профиль <br> (крайняя правая иконка снизу)</li>
									<li class="modal-avito__item">Номер будет указан в разделе «Телефоны и звонки»</li>
								</ul>
							</div>
							<div class="modal-avito__image">
								<img class="full-img" width="172" height="366" src="@/assets/img/avito/avito_mobile.webp" srcset="@/assets/img/avito/avito_mobile@2x.webp 2x" alt="изображение" />
							</div>
						</div>
					</div>
				</div>

				<!-- Призы -->
				<div class="modal-content" v-if="modalId === 'prizes'">
					<div class="modal-prizes" v-html="modalObj.descGetPrize"></div>
				</div>

				<!-- Информация о получении -->
				<div class="modal-content" v-if="modalId === 'quizNotFound'">
					<h5 class="modal-title --center">{{ modalObj.text }}</h5>
					<QuizCloseWebAppBtn v-if="modalObj.showBtnExitApp" />
				</div>
			</div>
		</Transition>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.v-enter-active,
.v-leave-active {
	transition: 0.5s ease;
}

.v-enter-from {
	opacity: 0;
	transform: translateY(-10%);
}
.v-leave-to {
	opacity: 0;
	transform: translateY(-10%);
}

.modal {
	--transition-time: 0.3s;
	position: fixed;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	background-color: rgba(0, 0, 0, 0.5);
	backdrop-filter: blur(10.800000190734863px);
	cursor: pointer;
	overflow-y: auto;
	overflow-x: hidden;
	text-align: center;
	opacity: 0;
	visibility: hidden;
	transition: opacity var(--transition-time), visibility var(--transition-time);
	&.--white-modal {
		background: var(--native-secondary-bg-color);
	}
}

.modal::before {
	content: "";
	display: inline-block;
	vertical-align: middle;
	height: 100%;
}

.modal__container {
	position: relative;
	width: 507px;
	margin: 50px auto;
	display: none;
	vertical-align: middle;
	background: var(--native-bg-color);
	border-radius: 16px;
	cursor: default;
	text-align: left;
	padding: 0;
	@media (max-width: vars.$xx) {
		width: 95%;
	}
	&.--w600 {
		width: 600px;
		@media (max-width: 640px) {
			width: 95%;
		}
		.modal-content {
			padding: 24px 38px;
			gap: 40px;
			@media (max-width: 640px) {
				padding: 24px;
				gap: 15px;
			}
		}
	}
	&.--small {
		width: 520px;
		@media (max-width: vars.$xx) {
			width: 95%;
		}
	}
}

.modal.is-open {
	opacity: 1;
	visibility: visible;
	transition: opacity var(--transition-time), visibility var(--transition-time);
	.modal__container {
		display: inline-block;
	}
}

.modal__container.modal-open {
	display: inline-block;
}

.fade {
	opacity: 0;
	transition: opacity var(--transition-time);
}

.fade.animate-open {
	opacity: 1;
	transition: opacity var(--transition-time);
}

.fadeInUp {
	opacity: 0;
	transform: translateY(-100px);
	transition: opacity var(--transition-time), transform var(--transition-time);
}

.fadeInUp.animate-open {
	opacity: 1;
	transform: translateY(0);
	transition: opacity var(--transition-time), transform var(--transition-time);
}

.disable-scroll {
	position: relative;
	overflow: hidden;
	height: 100vh;
	position: fixed;
	left: 0;
	top: 0;
	width: 100%;
}

.modal-close {
	position: absolute;
	width: 32px;
	height: 32px;
	top: -40px;
	right: -30px;
	@media (max-width: vars.$m) {
		right: 10px;
	}
	svg {
		width: 3.2rem;
		height: 3.2rem;
		stroke-width: 2px;
		stroke: #707579;
		transition: transform var(--transition) ease-in;
	}
	@media (hover: hover) {
		&:hover {
			svg {
				transform: scale(1.1);
			}
		}
	}
}

.modal-content {
	display: grid;
	gap: 15px;
	padding: 24px;
}
.modal-form {
	display: grid;
	gap: 0px;
}
.modal-title {
	margin-bottom: 5px;
}
.modal-subtitle {
	font-weight: 400;
	font-size: 20px;
	line-height: 160%;
	@media (max-width: vars.$xx) {
		font-size: 16px;
	}
	a {
		color: inherit;
		@media (hover: hover) {
			&:hover {
				text-decoration: underline;
			}
		}
	}
	&.--center {
		text-align: center;
	}
}
.buttons {
	display: grid;
	gap: 56px;
}

.modal-content__desc {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	color: var(--fill-default-1);
	@media (max-width: vars.$x480) {
		font-size: 1.8rem;
	}
	ul {
		padding-left: 3rem;
		list-style-type: disc;
		@media (max-width: vars.$x480) {
			padding-left: 1.5rem;
		}
		li {
		}
	}
}
.modal-content__desc-title {
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	margin-bottom: 4rem;
	@media (max-width: vars.$x480) {
		font-size: 1.8rem;
		margin-bottom: 1rem;
	}
}

.modal-avito {
	display: grid;
	gap: 44px;
}
.modal-avito__block {
	display: grid;
	gap: 24px;
	&.--mobile {
		grid-template-columns: 1fr 172px;
		gap: 2.1rem;
		align-items: start;
		@media (max-width: vars.$x480) {
			grid-template-columns: 1fr;
			justify-items: center;
		}
		.modal-avito__image {
			@media (max-width: vars.$x480) {
				width: 200px;
			}
		}
	}
}
.modal-avito__header {
	display: grid;
	gap: 17px;
}
.modal-avito__title {
	font-weight: 400;
	font-size: 1.6rem;
	line-height: 131%;
	letter-spacing: -0.01em;
	color: var(--native-hint-color);
}
.modal-avito__list {
	display: grid;
	gap: 10px;
	counter-reset: number;
}
.modal-avito__item {
	position: relative;
	font-weight: 400;
	font-size: 1.6rem;
	line-height: 131%;
	letter-spacing: -0.01em;
	color: var(--native-text-color);
	padding-left: 30px;
	a {
		color: var(--native-link-color);
		@media (hover: hover) {
			&:hover {
				text-decoration: underline;
			}
		}
	}
	&::before {
		position: absolute;
		top: 0px;
		left: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		counter-increment: number;
		content: counter(number);
		border-radius: 50%;
		width: 18px;
		height: 18px;
		background: var(--native-button-color);
		flex-shrink: 0;

		font-weight: 400;
		font-size: 1.3rem;
		line-height: 123%;
		letter-spacing: -0.01em;
		color: #fff;
	}
}
.modal-avito__image {
	overflow: hidden;
	border-radius: 9px;
}
</style>
